import { type CollectionTypology } from '@prisma/client'
import { createContext, useContext, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { type To } from 'react-router'
import routes from '#routes/index.js'
import { type SerializeFrom } from '#types/common'

type NavigationMenuOptions = Record<
	string,
	string | Record<string, string | { route: To; className?: string }>
>

type RouteContent = {
	label: string
	to?: string
}[]
interface PublicContextProps {
	navigation: {
		items: NavigationMenuOptions
		setSelected: (selected: keyof NavigationMenuOptions) => void
		open: boolean
		toggle: () => void
		selected: keyof NavigationMenuOptions
		route: {
			show: boolean
			content: RouteContent
			setShow: (b: boolean) => void
			setContent: (s: RouteContent) => void
		}
	}
}

const PublicContext = createContext<PublicContextProps>({
	navigation: {
		items: {},
		setSelected: () => {},
		open: false,
		toggle: () => {},
		route: {
			show: false,
			content: [],
			setShow: () => {},
			setContent: () => {},
		},
		selected: '',
	},
})

export const PublicContextProvider = ({
	children,
	typologies,
}: {
	typologies: SerializeFrom<
		CollectionTypology & {
			name: string
		}
	>[]
	children: React.ReactNode
}) => {
	const [navigation, setNavigation] = useState<
		PublicContextProps['navigation']
	>({
		items: {},
		setSelected: () => {},
		open: false,
		toggle: () => {},
		route: {
			show: true,
			content: [],
			setContent: () => {},
			setShow: () => {},
		},
		selected: '',
	})
	const { t } = useTranslation(['common', 'professional'])

	const items: NavigationMenuOptions = {
		product: Object.fromEntries([
			[
				t('common:actions.viewAll'),
				{
					route: routes.public.products.list.route({}),
					className: 'lowercase !pb-md block ',
				},
			],
			...typologies.map((t) => [
				t.name,
				`${routes.public.products.list.route({})}?typology=${t.code}`,
			]),
			...[
				[
					'DRESS CODE',
					{
						route: routes.public.landings.dressYourLook.route({}),
						className: 'italic',
					},
				],
			],
		]),
		sapienstone: Object.fromEntries([
			[t('to.sapienstone.what'), routes.public.about.route({})],
			// [t('to.design'), routes.public.landings.design.route({})],
			[t('to.innovation'), routes.public.landings.innovation.route({})],
			[t('to.sustainability'), routes.public.landings.sustainability.route({})],
		]),
		fourthDimension: routes.public.landings.fourthDimension.route({}),
		professional: Object.fromEntries([
			[
				t('common:actions.viewAll'),
				{
					route: routes.public.professional.route({}),
					className: 'lowercase !pb-md block ',
				},
			],
			[
				t('professional:samples.title'),
				{
					route: {
						pathname: routes.public.professional.route({}),
						hash: 'samples',
					},
				},
			],
			[
				t('professional:warranty.title'),
				{
					route: {
						pathname: routes.public.professional.route({}),
						hash: 'warranty',
					},
				},
			],
			[
				t('to.downloads.technicalDocs'),
				{
					route: {
						pathname: routes.public.professional.route({}),
						hash: 'technical-documentation',
					},
				},
			],
			[
				t('to.professional.3dConfigurator'),
				{
					route: {
						pathname: routes.public.professional.route({}),
						hash: '3d-configurator',
					},
				},
			],
		]),
		magazine: routes.public.blog.index.route({}),
		downloads: Object.fromEntries([
			[
				t('common:actions.viewAll'),
				{
					route: routes.public.downloads.route({}),
					className: 'lowercase !pb-md block ',
				},
			],
			[
				t('to.downloads.catalogs'),
				{
					route: {
						pathname: routes.public.downloads.route({}),
						hash: 'catalogs',
					},
				},
			],
			[
				t('to.downloads.technicalDocs'),
				{
					route: {
						pathname: routes.public.downloads.route({}),
						hash: 'technical-documentation',
					},
				},
			],
			[
				t('to.downloads.certificates'),
				{
					route: {
						pathname: routes.public.downloads.route({}),
						hash: 'environmental-certificates',
					},
				},
			],
			[
				t('to.downloads.3dLibrary'),
				{
					route: {
						pathname: routes.public.downloads.route({}),
						hash: '3d-software-library',
					},
				},
			],
		]),
		contact: Object.fromEntries([
			[t('to.contact.query'), routes.public.contact.route({})],
			[
				t('to.contact.showrooms'),
				{
					route: {
						pathname: routes.public.contact.route({}),
						hash: 'showrooms',
					},
				},
			],
			[
				t('to.contact.faq'),
				{
					route: {
						pathname: routes.public.contact.route({}),
						hash: 'faq',
					},
				},
			],
		]),
	}

	return (
		<PublicContext.Provider
			value={{
				navigation: {
					...navigation,
					items,
					setSelected: (selected: keyof NavigationMenuOptions) => {
						if (selected === navigation.selected)
							return setNavigation((prev) => ({
								...prev,
								selected: '',
							}))
						setNavigation((prev) => ({ ...prev, selected }))
					},
					toggle: () => {
						setNavigation((prev) => ({ ...prev, open: !prev.open }))
					},
					route: {
						...navigation.route,
						setShow: (show) => {
							setNavigation((prev) => ({
								...prev,
								route: { ...prev.route, show },
							}))
						},
						setContent: (content) => {
							setNavigation((prev) => ({
								...prev,
								route: { ...prev.route, content: content },
							}))
						},
					},
				},
			}}
		>
			{children}
		</PublicContext.Provider>
	)
}

export const usePublicContext = () => {
	const context = useContext(PublicContext)

	if (context === undefined) {
		throw new Error(
			'usePublicContext must be used within a PublicContextProvider',
		)
	}

	return context
}
